import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

// Phone number regex validation (French local or international E.164)
export const phoneRegex = /^(?:(?:\+|00)?[1-9]\d{6,14}|0[1-9]\d{8})$/;

// Hook to create phone schema with translations
export const usePhoneFormSchema = (phoneRequired = true) => {
  const { t } = useTranslation();

  return z.object({
    phone: z
      .string()
      .refine(
        phone => (phone === '' ? !phoneRequired : phoneRegex.test(phone.replace(/[\s.-]/g, ''))),
        {
          message: t('formErrors.phone'),
        },
      ),
  });
};

export type PhoneFormData = z.infer<ReturnType<typeof usePhoneFormSchema>>;

export const usePhoneForm = (initialData?: Partial<PhoneFormData>, phoneRequired = true) => {
  const phoneSchema = usePhoneFormSchema(phoneRequired);

  const form = useForm<PhoneFormData>({
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      phone: initialData?.phone || '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    form.reset({ phone: initialData?.phone || '' });
  }, [form, initialData]);

  return {
    form,
  };
};
